import React, { useEffect, useState } from "react";
import { api, money } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash2, DollarSign, Package, Music, Eye } from "lucide-react";
import { toast } from "sonner";
import { IcalActions } from "./tabs/TimelineTab";

const DEL_STATUS = [
  { value: "pending", label: "Pending" },
  { value: "in-progress", label: "In progress" },
  { value: "delivered", label: "Delivered" },
  { value: "approved", label: "Approved" },
];

export default function VendorDetailDialog({ eventId, member, open, onOpenChange, canEdit, onSaved, onPreviewAs }) {
  const [tab, setTab] = useState("admin");
  const [payment, setPayment] = useState({});
  const [deliverables, setDeliverables] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [songRequests, setSongRequests] = useState([]);
  const [newDel, setNewDel] = useState({ title: "", due_date: "", status: "pending", notes: "" });

  const isDJ = member && ["DJ / Band", "DJ", "Band"].includes(member.vendor_category);

  useEffect(() => {
    if (!member || !open) return;
    setPayment({
      contract_amount: member.contract_amount || 0,
      deposit_amount: member.deposit_amount || 0,
      deposit_paid: !!member.deposit_paid,
      balance_paid: !!member.balance_paid,
      contract_notes: member.contract_notes || "",
    });
    (async () => {
      try {
        const [d, t, tl] = await Promise.all([
          api.get(`/events/${eventId}/members/${member.id}/deliverables`),
          api.get(`/events/${eventId}/tasks`),
          api.get(`/events/${eventId}/timeline`),
        ]);
        setDeliverables(d.data);
        setTasks(t.data.filter(x => x.assignee_member_id === member.id));
        setTimeline(tl.data.filter(x => x.vendor_member_id === member.id));
      } catch (e) { console.error("VendorDetail load failed:", e); }
      // Song requests: only fetch when DJ / Band vendor
      if (["DJ / Band", "DJ", "Band"].includes(member.vendor_category)) {
        try {
          const { data } = await api.get(`/events/${eventId}/song-requests`);
          setSongRequests(data);
        } catch (e) { console.error("song-requests load failed:", e); }
      } else {
        setSongRequests([]);
      }
    })();
  }, [member, eventId, open]);

  const savePayment = async () => {
    try {
      await api.patch(`/events/${eventId}/members/${member.id}`, {
        ...payment,
        contract_amount: Number(payment.contract_amount) || 0,
        deposit_amount: Number(payment.deposit_amount) || 0,
      });
      toast.success("Vendor details saved");
      onSaved && onSaved();
    } catch (e) { toast.error("Could not save"); }
  };

  const addDeliverable = async () => {
    if (!newDel.title) { toast.error("Title required"); return; }
    try {
      const { data } = await api.post(`/events/${eventId}/members/${member.id}/deliverables`, {
        ...newDel, due_date: newDel.due_date || null,
      });
      setDeliverables([...deliverables, data]);
      setNewDel({ title: "", due_date: "", status: "pending", notes: "" });
      toast.success("Deliverable added");
    } catch (e) { toast.error("Could not add"); }
  };

  const updDeliverable = async (id, field, value) => {
    try {
      await api.patch(`/events/${eventId}/members/${member.id}/deliverables/${id}`, { [field]: value });
      setDeliverables(deliverables.map(d => d.id === id ? { ...d, [field]: value } : d));
    } catch (e) { toast.error("Could not update"); }
  };

  const delDeliverable = async (id) => {
    if (!confirm("Delete this deliverable?")) return;
    try {
      await api.delete(`/events/${eventId}/members/${member.id}/deliverables/${id}`);
      setDeliverables(deliverables.filter(d => d.id !== id));
    } catch (e) { toast.error("Could not delete"); }
  };

  if (!member) return null;

  const balance = (payment.contract_amount || 0) - (payment.deposit_amount || 0);
  const paidTotal = (payment.deposit_paid ? (payment.deposit_amount || 0) : 0) + (payment.balance_paid ? balance : 0);
  const outstanding = (payment.contract_amount || 0) - paidTotal;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="eyebrow">{member.vendor_category || member.role}</div>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="font-serif text-3xl text-app" data-testid="vendor-detail-name">{member.name}</DialogTitle>
            {onPreviewAs && (
              <button
                type="button"
                onClick={() => { onPreviewAs(member); onOpenChange(false); }}
                data-testid="vendor-detail-preview-as"
                className="shrink-0 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase px-2.5 py-1.5 border border-hair hover:border-[#F59E0B] hover:text-[#F59E0B] text-app transition-colors"
                title={`View this event as ${member.name} would see it`}
              >
                <Eye size={12} strokeWidth={2} /> View as
              </button>
            )}
          </div>
          <DialogDescription className="text-sm text-muted font-light">{member.email}{member.phone ? ` · ${member.phone}` : ""}</DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="border-b border-hair flex gap-6 flex-wrap">
          {[
            ["admin","Admin & Payment"],
            ["deliverables","Deliverables"],
            ["schedule","Their schedule"],
            ...(isDJ ? [["songs","Song Requests"]] : []),
          ].map(([v,l]) => (
            <button
              key={v}
              onClick={() => setTab(v)}
              data-testid={`vendor-detail-tab-${v}`}
              className={`py-3 text-sm border-b-2 transition-colors ${
                tab === v ? "border-[#0A0A0A] text-app" : "border-transparent text-muted hover:text-app"
              }`}
            >{l}</button>
          ))}
        </div>

        {tab === "admin" && (
          <div className="space-y-5 py-4">
            <div className="grid grid-cols-3 gap-4">
              <Card label="Contract total" value={money(payment.contract_amount)} />
              <Card label="Paid" value={money(paidTotal)} accent="text-[#059669]" />
              <Card label="Outstanding" value={money(outstanding)} accent={outstanding > 0 ? "text-[#EF4444]" : "text-[#059669]"} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Contract amount ($)">
                <Input
                  data-testid="vendor-contract-input"
                  type="number"
                  value={payment.contract_amount}
                  onChange={(e) => setPayment({ ...payment, contract_amount: e.target.value })}
                  disabled={!canEdit}
                  className="rounded-sm"
                />
              </Field>
              <Field label="Deposit amount ($)">
                <Input
                  type="number"
                  value={payment.deposit_amount}
                  onChange={(e) => setPayment({ ...payment, deposit_amount: e.target.value })}
                  disabled={!canEdit}
                  className="rounded-sm"
                />
              </Field>
            </div>

            <div className="flex items-center gap-6 py-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  data-testid="vendor-deposit-paid"
                  checked={payment.deposit_paid}
                  onCheckedChange={(v) => setPayment({ ...payment, deposit_paid: !!v })}
                  disabled={!canEdit}
                  className="rounded-sm border-hair-strong data-[state=checked]:bg-app data-[state=checked]:border-[#0A0A0A]"
                />
                <span className="text-sm">Deposit paid</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  data-testid="vendor-balance-paid"
                  checked={payment.balance_paid}
                  onCheckedChange={(v) => setPayment({ ...payment, balance_paid: !!v })}
                  disabled={!canEdit}
                  className="rounded-sm border-hair-strong data-[state=checked]:bg-app data-[state=checked]:border-[#0A0A0A]"
                />
                <span className="text-sm">Balance paid</span>
              </label>
            </div>

            <Field label="Contract notes">
              <Textarea
                rows={3}
                value={payment.contract_notes}
                onChange={(e) => setPayment({ ...payment, contract_notes: e.target.value })}
                disabled={!canEdit}
                className="rounded-sm"
                placeholder="Payment schedule, cancellation policy, contact details…"
              />
            </Field>

            {canEdit && (
              <div className="flex justify-end">
                <Button data-testid="vendor-save-admin" onClick={savePayment} className="rounded-sm bg-app hover:bg-[#000000]">
                  Save vendor details
                </Button>
              </div>
            )}
          </div>
        )}

        {tab === "deliverables" && (
          <div className="space-y-4 py-4">
            {canEdit && (
              <div className="bg-canvas p-4 rounded-sm border border-hair">
                <div className="eyebrow mb-3">Add deliverable</div>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_150px_120px] gap-2">
                  <Input
                    data-testid="deliverable-title-input"
                    value={newDel.title}
                    onChange={(e) => setNewDel({ ...newDel, title: e.target.value })}
                    placeholder="e.g. Final head count due"
                    className="rounded-sm"
                  />
                  <Input
                    type="date"
                    value={newDel.due_date}
                    onChange={(e) => setNewDel({ ...newDel, due_date: e.target.value })}
                    className="rounded-sm"
                  />
                  <Button data-testid="add-deliverable-btn" onClick={addDeliverable} className="rounded-sm bg-app hover:bg-[#000000]">
                    <Plus size={14} strokeWidth={1.5} className="mr-1" /> Add
                  </Button>
                </div>
              </div>
            )}

            {deliverables.length === 0 ? (
              <div className="text-center py-10 text-muted font-light">No deliverables yet.</div>
            ) : (
              <div className="border border-hair rounded-sm divide-y divide-[color:var(--hair)]/60">
                {deliverables.map(d => (
                  <div key={d.id} data-testid={`deliverable-${d.id}`} className="p-3 flex items-center gap-3">
                    <Package size={14} strokeWidth={1.5} className="text-muted shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-app">{d.title}</div>
                      {d.due_date && <div className="text-xs text-muted font-light">Due {new Date(d.due_date).toLocaleDateString(undefined, { month:"short", day:"numeric"})}</div>}
                    </div>
                    <Select value={d.status} onValueChange={(v) => updDeliverable(d.id, "status", v)}>
                      <SelectTrigger className="w-36 h-8 rounded-sm text-xs" data-testid={`deliverable-status-${d.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{DEL_STATUS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                    {canEdit && (
                      <button onClick={() => delDeliverable(d.id)} data-testid={`delete-deliverable-${d.id}`} className="text-muted hover:text-[#EF4444]">
                        <Trash2 size={14} strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "schedule" && (
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between">
              <div className="eyebrow">Vendor calendar</div>
              <IcalActions eventId={eventId} memberId={member.id} />
            </div>

            <section>
              <div className="eyebrow mb-3">Assigned tasks ({tasks.length})</div>
              {tasks.length === 0 ? (
                <div className="text-sm text-muted font-light">No tasks assigned to this vendor yet.</div>
              ) : (
                <div className="border border-hair rounded-sm divide-y divide-[color:var(--hair)]/60">
                  {tasks.map(t => (
                    <div key={t.id} className="p-3 flex items-center justify-between">
                      <div>
                        <div className={t.status === "done" ? "line-through text-muted" : "text-app"}>{t.title}</div>
                        {t.due_date && <div className="text-xs text-muted">Due {new Date(t.due_date).toLocaleDateString(undefined, { month:"short", day:"numeric"})}</div>}
                      </div>
                      <span className="eyebrow">{t.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <div className="eyebrow mb-3">Day-of timeline ({timeline.length})</div>
              {timeline.length === 0 ? (
                <div className="text-sm text-muted font-light">No timeline moments assigned.</div>
              ) : (
                <div className="border border-hair rounded-sm divide-y divide-[color:var(--hair)]/60">
                  {timeline.sort((a,b) => (a.time||"").localeCompare(b.time||"")).map(b => (
                    <div key={b.id} className="p-3 grid grid-cols-[80px_1fr] gap-3">
                      <div className="font-serif text-lg text-app">{b.time}</div>
                      <div>
                        <div className="text-app">{b.title}</div>
                        {b.description && <div className="text-xs text-muted font-light">{b.description}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {tab === "songs" && (
          <div className="space-y-4 py-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="eyebrow flex items-center gap-1.5"><Music size={12} strokeWidth={1.5} /> From your guests</div>
                <div className="font-serif text-2xl text-app">{songRequests.length} requests</div>
              </div>
              <div className="text-xs text-muted font-light max-w-xs text-right">
                Song requests roll in automatically as guests RSVP through the public microsite.
              </div>
            </div>

            {songRequests.length === 0 ? (
              <div className="text-center py-10 text-muted font-light">No song requests yet.</div>
            ) : (
              <div className="border border-hair rounded-sm divide-y divide-[color:var(--hair)]/60">
                {songRequests.map(s => (
                  <div key={s.id} data-testid={`song-request-${s.id}`} className="p-3 grid grid-cols-[1fr_140px] gap-3 items-start">
                    <div>
                      <div className="text-app">{s.song_request}</div>
                      <div className="text-xs text-muted font-light mt-0.5">Requested by {s.name}{s.party_size > 1 ? ` (party of ${s.party_size})` : ""}</div>
                    </div>
                    <div className="text-right">
                      <span className={`eyebrow px-2 py-0.5 rounded-sm ${
                        s.rsvp_status === "yes" ? "bg-surface-2 text-[#059669]" :
                        s.rsvp_status === "no" ? "bg-[#EF4444]/10 text-[#EF4444]" :
                        "bg-canvas text-muted"
                      }`}>{s.rsvp_status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Card({ label, value, accent }) {
  return (
    <div className="bg-canvas border border-hair rounded-sm p-4">
      <div className="eyebrow mb-1.5">{label}</div>
      <div className={`font-serif text-2xl ${accent || "text-app"}`}>{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (<div><Label className="eyebrow block mb-2">{label}</Label>{children}</div>);
}
